import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Phone, Mail, Linkedin, Clipboard, Check } from 'lucide-react';
import { getInitials } from './ProfilePage';
import { codeToEmoji } from '../flags';
import { majorMap, minorMap, certMap } from '../mapping';
import { getSectorEmoji } from '../sectorData';


const UserCard = ({ user, index, handleLink}) => {
  const [hoveredPhone, setHoveredPhone] = useState(false);
  const [hoveredEmail, setHoveredEmail] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [notification, setNotification] = useState({ show: false, text: '' });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const hasGraduated = (gradYear) => {
    const today = new Date();
    const graduationDate = new Date(`${gradYear}-06-01`);
    return today >= graduationDate;
  };

  let parsedGrad = "";
  if (user.gradYear) {
    parsedGrad = "'" + user.gradYear.toString().slice(-2);
  }


  let lst = [];
  if (user.location) {
    lst.push(user.location);
  }
  let sectorList = [];
  if (user.sectors) {
    user.sectors.forEach(sector => {
      sectorList.push(getSectorEmoji(sector));
    })
  }
  const sectorEmojis = sectorList.join("")
  
  if (sectorEmojis !== "") {
    lst.push(sectorEmojis)
  }

  let locationSector = null;
  if (lst.length > 0) {
    locationSector = lst.join(" - ");
  }

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'phone') {
        setCopiedPhone(true);
        setTimeout(() => setCopiedPhone(false), 2000);
      } else {
        setCopiedEmail(true);
        setTimeout(() => setCopiedEmail(false), 2000);
      }
      setNotification({ show: true, text: 'Successfully copied to clipboard' });
      setTimeout(() => setNotification({ show: false, text: '' }), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleLinkedInClick = (e, url) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(url, '_blank');
  };

  return (
    <>
      <a 
          href={`/profile/${user.id}`}
          onClick={(e) => handleLink(e, `/profile/${user.id}`)}
          className="no-underline relative"
          style={index}
        >
        <Card>
          <CardContent className="flex items-center justify-between pl-3 pr-3 pt-4 pb-4">
            <div className="relative flex flex-col items-center ml-1">
              <Avatar className="h-20 w-20">
                <AvatarImage loading="lazy" onLoad={() => setImageLoaded(true)}
                  onError={() => setImageError(true)}
                  style={{
                    opacity: imageLoaded ? 1 : 0,
                    transition: 'opacity 0.3s ease-in-out'
                  }} src={user.image} />
                <AvatarFallback>{!imageLoaded || imageError ? getInitials(user.name) : null}</AvatarFallback>
              </Avatar>
              {parsedGrad && (
                <div className="absolute -top-2 -right-2 bg-slate-200 rounded-full w-7 h-7 flex items-center justify-center">
                  <span className="text-xs font-medium text-slate-700">{parsedGrad}</span>
                </div>
              )}
              {user.nationalities && user.nationalities.length > 0 && (
                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
                  {user.nationalities.slice(0, 3).map((code, index) => (
                    <span key={index} className="bg-slate-200 rounded-full w-7 h-7 flex items-center justify-center text-base">
                      {codeToEmoji(code)}
                    </span>
                  ))}
                </div>
              )}
            </div>
            
            <div className="ml-5 flex-grow">
              <h2 className="text-lg font-semibold">{user.name}</h2>
              <p className="text-xs text-muted-foreground"><em>{user.blurb}</em></p>
              {(user.currentCompany || user.currentRole) && 
                <p className="text-sm text-muted-foreground">{user.currentRole && user.currentRole} {user.currentCompany && <span>@ {user.currentCompany}</span>}</p>
              }
              {user.gradYear && hasGraduated(user.gradYear) && locationSector &&
                <p className="text-sm text-muted-foreground">{locationSector}</p>
              }
              <div className="flex flex-row justify-start items-center gap-1 mt-1">
              {user.gradYear && !hasGraduated(user.gradYear) && user.majors &&
                <span className="flex flex-row justify-start items-center gap-1">
                {user.majors.map((major, index) => (
                  <div
                  key={index}
                  className="relative group bg-indigo-100 w-fit rounded-lg px-2 py-1 text-sm text-muted-foreground"
                >
                  {majorMap.get(major)}
                  <div className="absolute bottom-full left-1/2 z-10 -translate-x-1/2 mb-1 hidden group-hover:block bg-slate-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                    {major}
                  </div>
                </div>
                ))}
              </span>
              }
              {user.gradYear && !hasGraduated(user.gradYear) && user.minors &&
                <span className="flex flex-row justify-start items-center gap-1">
                {user.minors.map((minor, index) => (
                  <div
                  key={index}
                  className="relative group bg-green-100 w-fit rounded-lg px-2 py-1 text-sm text-muted-foreground"
                >
                  {minorMap.get(minor)}
                  <div className="absolute bottom-full left-1/2 z-10 -translate-x-1/2 mb-1 hidden group-hover:block bg-slate-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                    {minor}
                  </div>
                </div>
                ))}
              </span>
              }
              {user.gradYear && !hasGraduated(user.gradYear) && user.certificates &&
                <span className="flex flex-row justify-start items-center gap-1">
                {user.certificates.map((cert, index) => (
                  <div
                  key={index}
                  className="relative group bg-amber-100 w-fit rounded-lg px-2 py-1 text-sm text-muted-foreground"
                >
                  {certMap.get(cert)}
                  <div className="absolute bottom-full left-1/2 z-10 -translate-x-1/2 mb-1 hidden group-hover:block bg-slate-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                    {cert}
                  </div>
                </div>
                ))}
              </span>
              }
              </div>
              
            </div>

            <div className="flex flex-col items-center gap-4 pl-3 ml-1 border-l">
              {user.phone && (
                <div className="relative group">
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      copyToClipboard(user.phone, 'phone');
                    }}
                    className="cursor-pointer"
                    onMouseEnter={() => setHoveredPhone(true)}
                    onMouseLeave={() => setHoveredPhone(false)}
                  >
                    {copiedPhone ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : hoveredPhone ? (
                      <Clipboard className="w-4 h-4 text-slate-600 hover:text-slate-900" />
                    ) : (
                      <Phone className="w-4 h-4 text-slate-600 hover:text-slate-900" />
                    )}
                  </button>
                  <div className="absolute bottom-full left-1/2 z-1000 -translate-x-1/2 mb-2 hidden group-hover:block bg-slate-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                    {user.phone}
                  </div>
                </div>
              )}
              
              {user.email && (
                <div className="relative group">
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      copyToClipboard(user.email, 'email');
                    }}
                    className="cursor-pointer"
                    onMouseEnter={() => setHoveredEmail(true)}
                    onMouseLeave={() => setHoveredEmail(false)}
                  >
                    {copiedEmail ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : hoveredEmail ? (
                      <Clipboard className="w-4 h-4 text-slate-600 hover:text-slate-900" />
                    ) : (
                      <Mail className="w-4 h-4 text-slate-600 hover:text-slate-900" />
                    )}
                  </button>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-slate-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                    {user.displayEmail || user.email}
                  </div>
                </div>
              )}
              
              {user.linkedIn && (
                <button 
                  onClick={(e) => handleLinkedInClick(e, user.linkedIn)}
                  className="cursor-pointer"
                >
                  <Linkedin className="w-4 h-4 text-slate-600 hover:text-slate-900" />
                </button>
              )}
            </div>
          </CardContent>
        </Card>
      </a>

      {notification.show && (
        <div className="fixed bottom-4 right-4 bg-slate-800 text-white px-4 py-2 rounded-md shadow-lg text-sm">
          {notification.text}
        </div>
      )}
    </>
  );
};

export default UserCard;